import { useEffect, useState, type MouseEvent } from 'react';
import { api, errorCode, errorMessage } from '../lib/api.ts';
import { useActiveTurns, stopTurn } from '../lib/chatTurns.ts';
import { notifyActivityChanged } from '../lib/useDeckActivity.ts';
import { sentenceCase } from '../lib/format.ts';
import { groupSettled, rowId, undoCountsByRow, currentBySlide } from '../lib/activityStacks.ts';
import FlipPills from '../components/FlipPills.tsx';
import { useFlipShowing } from '../lib/reviewFlip.ts';
import { findingSummary } from '../lib/geometryAsk.ts';
import { flaggedSlides } from '../lib/useDeckFindings.ts';
import {
  canPreviewRow,
  PREVIEWABLE_TOOLS,
  previewRow,
  toggleRow,
  applyRow,
  rejectRow,
  revertRow,
  revertPreviewRow,
  confirmRevertRow,
  cancelRowPreview,
} from '../lib/proposalActions.ts';
import { StopSquare } from '../lib/icons.tsx';
import type { SlideRow } from '../lib/proposalActions.ts';
import type { ActiveTurn, StopTarget } from '../lib/chatTurns.ts';
import type { ActivityStack as Stack } from '../lib/activityStacks.ts';
import type { ActiveRunRow, ActivityRow as Row, DeckActivityPayload } from '../../shared/api.ts';
import type { LayoutFinding, SlideEntry } from '../../shared/deck.ts';

/**
 * Where a jump lands. `slideId` is null for the whole-deck conversation.
 *
 * `kind` is wider than 'slide' | 'deck' because that is what actually reaches
 * Canvas's `handleJumpToTurn`: it branches on 'deck' OR 'selection', and a
 * running turn's kind may be null before its scope is known — which that
 * handler reads as the slide case, the same way the row's own tooltip does.
 */
export type JumpTarget = { kind: 'slide' | 'deck' | 'selection' | null; slideId: string | null };

/** What the Canvas toast is told after an action settles. */
export type ActionNotice = { kind: 'revert' | 'reapply'; title: string };

/**
 * One row of the Running group. The two sources are merged rather than
 * concatenated: a local turn carries the streaming text, the server's row
 * carries the clock, and only the server's survives a refresh.
 */
export type RunningRow = Pick<ActiveTurn, 'key' | 'slideId' | 'slideIds' | 'label'> & {
  kind: ActiveTurn['kind'];
  /** The two sources report progress differently — a local `TurnProgress` also
   *  carries `doneIds` — and the meta line only ever reads the counts, so this
   *  is deliberately the narrower of the two rather than either one's shape. */
  progress: { done: number; total: number } | null;
  startedAt: number | null;
  hardMs: number | null;
  /** Set only on a server-only row: there is no local fetch to abort, so it
   *  stops the run by address instead. */
  stopTarget?: StopTarget;
};

/**
 * The Activity tab — the one place to see every AI change across the deck.
 *
 * Three groups: turns RUNNING right now (from the in-memory turn store, with
 * stop + jump), proposals AWAITING REVIEW (live on the slide by default —
 * flip Original ⇄ Proposed, Apply or Reject), then the HISTORY of
 * applied/rejected/reverted changes. Revert and Re-apply also review live:
 * the slide shows what you would end up with, and you confirm or keep the
 * current version. Rows come from /api/chat/:slug/history and refetch when
 * any turn completes or any surface acts (studio:activity-refresh).
 *
 * Settled changes are STACKED PER SLIDE (see `groupSettled`). Six edits to one
 * slide used to read as six peers, each with its own Revert — but they are not
 * peers, they are a version ladder: undoing an older one rewinds past the
 * newer ones. The stack says so, and every undo button is named for how far
 * back it goes (Jake, 2026-07-31).
 */
type ActivityPanelProps = {
  slug: string;
  slides: SlideEntry[];
  /** `useDeckActivity`'s state. Partial because the panel defends with `|| []`
   *  on both lists rather than trusting the first render. */
  activity?: Partial<DeckActivityPayload> | null;
  onJumpToTurn?: (target: JumpTarget) => void;
  onChanged?: (notice?: ActionNotice) => void;
  /** Canvas owns the toast, so it handles the stop when mounted there. */
  onStopRun?: (run: RunningRow) => void;
  /** `useDeckFindings`' state — `pending` is the slides not yet measured. */
  findings?: { findings?: Record<string, LayoutFinding[]>; pending?: string[] } | null;
};

export default function ActivityPanel({
  slug,
  slides,
  activity,
  onJumpToTurn,
  onChanged,
  onStopRun,
  findings,
}: ActivityPanelProps) {
  const live = useActiveTurns(slug);
  // Deck-wide layout findings (pipeline/slide-findings.ts). `checking` is how
  // many slides have not been measured yet — see the group's own note on why
  // that has to be visible.
  const flagged = flaggedSlides(findings?.findings, slides);
  const checking = (findings?.pending || []).length;
  const rows = activity?.rows || [];
  const awaitingAll = rows.filter((r) => r.status === 'ready' || (r.previewing && r.status !== 'rejected'));
  // ONE live decision per slide. An overflow rework re-proposes the SAME slide,
  // so a couple of rounds left two Keep buttons side by side for one slide —
  // and only the NEWEST proposal is live on disk, so keeping an older one
  // writes stale JSX over the newer edit (Jake, 2026-08-02). The chat pane has
  // always collapsed these (`supersededIds` in Conversation.tsx); this list
  // never did, which is also why the on-slide banner counted 1 while this
  // counted 2.
  //
  // Rows that AREN'T superseded still stay flat — each of those is its own
  // decision, which is why they are not stacked like settled changes are.
  // Rows arrive NEWEST FIRST (routes/chat.ts sorts `b.ts - a.ts`), so the first
  // one seen for a slide is the live one and everything after it is stale.
  //
  // The claim is staked over EVERY slide-content row, not just the undecided
  // ones. Scanning only `awaitingAll` meant deciding the newest proposal handed
  // the slide back to the one it replaced: keep the overflow rework and the
  // pre-rework proposal — never resolved, because the rework was automatic —
  // reappeared here with a live Keep on stale JSX (Jake, 2026-08-04). The chat
  // pane had the identical hole; both now let a settled row hold its slide.
  const supersededAwaiting = new Set<string>();
  const seenAwaitingSlide = new Set<string>();
  for (const r of rows) {
    if (!r.slideId || !PREVIEWABLE_TOOLS.has(r.tool)) continue;
    if (!seenAwaitingSlide.has(r.slideId)) seenAwaitingSlide.add(r.slideId);
    else supersededAwaiting.add(rowId(r));
  }
  const awaiting = awaitingAll.filter((r) => !supersededAwaiting.has(rowId(r)));
  const settled = rows.filter((r) => !awaitingAll.includes(r));
  // Both maps are deck-wide on purpose — a row under review has left its stack,
  // and the rows it left behind still have to count it.
  const undoCounts = undoCountsByRow(rows);
  const currentIds = currentBySlide(rows);

  // Ticks the elapsed times in the Running rows. A run with no visible clock is
  // what made a slow run look like a hung one, which is what got a healthy run
  // stopped in the first place.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Running rows come from two places and must not double up: this tab's own
  // turn store (live streaming text and progress) and the SERVER's list (the
  // only source that survives a page refresh, and the only one that sees a run
  // another tab started). Where both know a run, the local row wins but borrows
  // the server's clock — only the server knows when the run really began.
  const serverRuns = activity?.running || [];
  const serverByKey = new Map<string, ActiveRunRow>(serverRuns.map((r) => [`${r.slug}::${r.threadKey}`, r]));
  const threadKeyOf = (slideId: string | null) => (slideId ? String(slideId).split('#')[0] : '_deck');
  const claimed = new Set<string>();
  const localRunning: RunningRow[] = live
    .filter((t) => t.state === 'streaming')
    .map((t) => {
      const k = `${slug}::${threadKeyOf(t.slideId)}`;
      const server = serverByKey.get(k);
      if (server) claimed.add(k);
      return { ...t, startedAt: server?.startedAt ?? null, hardMs: server?.hardMs ?? null };
    });
  const serverOnly: RunningRow[] = serverRuns
    .filter((r) => !claimed.has(`${r.slug}::${r.threadKey}`))
    .map((r) => ({
      key: `server:${r.slug}::${r.threadKey}`,
      kind: r.scopeKind,
      slideId: r.scopeKind === 'slide' ? r.threadKey : null,
      slideIds: r.slideIds,
      progress: r.progress,
      startedAt: r.startedAt,
      hardMs: r.hardMs,
      label: null,
      // Started elsewhere, so there is no local fetch to abort — this row stops
      // the run by address instead.
      stopTarget: { slug: r.slug, threadKey: r.threadKey },
    }));
  const running = [...localRunning, ...serverOnly];

  const slideTitle = (id: string | null | undefined) => {
    if (!id) return 'Whole deck';
    // Selection ids are base (file-level); slide entries may carry "#n".
    const s = (slides || []).find((x) => x.id === id || x.id.split('#')[0] === id);
    return s?.title || id;
  };

  // "3m of 8m" — a run has a known time limit, so show the budget rather than
  // leaving the user to guess whether a quiet agent is thinking or wedged. That
  // guess is what got a healthy run stopped mid-batch (2026-07-30).
  const elapsedMeta = (t: RunningRow) => {
    if (!t.startedAt) return '';
    const mins = (ms: number) => `${Math.max(0, Math.floor(ms / 60_000))}m`;
    const secs = Math.floor((now - t.startedAt) / 1000);
    const spent = secs < 60 ? `${secs}s` : mins(now - t.startedAt);
    return t.hardMs ? ` · ${spent} of ${mins(t.hardMs)}` : ` · ${spent}`;
  };

  // Running meta: a selection turn shows live progress ("2 of 5 slides
  // written…") once the agent starts writing, the slide list before that;
  // single-scope turns keep the plain "working…".
  const runningMeta = (t: RunningRow) => {
    const clock = elapsedMeta(t);
    if (!t.slideIds || t.slideIds.length < 2) return `working…${clock}`;
    if (t.progress?.total) return `${t.progress.done} of ${t.progress.total} slides written…${clock}`;
    const titles = t.slideIds.map(slideTitle);
    const shown = titles.slice(0, 4);
    const more = titles.length - shown.length;
    return `editing ${shown.join(', ')}${more > 0 ? ` +${more} more` : ''}${clock}`;
  };

  const jump = (row: Row) =>
    onJumpToTurn?.({ kind: row.scopeKind === 'deck' ? 'deck' : 'slide', slideId: row.slideId });

  // Canvas owns the toast, so it handles the stop when mounted there; the bare
  // fallback keeps the button working wherever the panel is reused.
  const stopRun = (t: RunningRow) => (onStopRun ? onStopRun(t) : stopTurn(t.key, t.stopTarget ?? null));

  // Awaiting review stays FLAT: each proposal is its own decision, so stacking
  // them would hide work the user still has to act on.
  const renderRows = (list: Row[]) => (
    <ul className="activity-panel__list">
      {list.map((row) => (
        <ActivityRow
          key={row.toolId || row.historyId}
          slug={slug}
          row={row}
          title={slideTitle(row.slideId)}
          undoCount={undoCounts.get(rowId(row)) || 1}
          onJump={() => jump(row)}
          onChanged={onChanged}
        />
      ))}
    </ul>
  );

  return (
    <div className="activity-panel" role="tabpanel" aria-label="Activity">
      {running.length > 0 && (
        <div className="activity-panel__group">
          <div className="activity-panel__group-head">Running</div>
          <ul className="activity-panel__list">
            {running.map((t) => (
              <li
                key={t.key}
                className="activity-panel__row activity-panel__row--streaming activity-panel__row--clickable"
                onClick={() => onJumpToTurn?.({ kind: t.kind, slideId: t.slideId })}
                title={t.kind === 'deck' ? 'Show the whole-deck conversation' : 'Jump to this slide'}
              >
                <span className="activity-panel__dot activity-panel__dot--streaming" aria-hidden="true" />
                <div className="activity-panel__main">
                  <span className="activity-panel__label">
                    {t.label || (t.kind === 'deck' ? 'Whole deck' : slideTitle(t.slideId))}
                  </span>
                  <span className="activity-panel__meta muted small">{runningMeta(t)}</span>
                </div>
                <button
                  type="button"
                  className="iconbtn activity-panel__stop"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Stopping keeps whatever the agent had already finished —
                    // those slides come back as proposals to review.
                    stopRun(t);
                  }}
                  title="Stop this edit: finished slides are kept for review"
                  aria-label="Stop this edit"
                >
                  <StopSquare size={10} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {awaiting.length > 0 && (
        <div className="activity-panel__group">
          <div className="activity-panel__group-head">Awaiting review</div>
          {renderRows(awaiting)}
        </div>
      )}

      {/* Slides with a layout problem, ANYWHERE in the deck — the answer to
          "how would I ever know slide 31 is broken if I don't scroll to it".
          The live checks only see the slide you are looking at; these come from
          the thumbnail pass, which renders every slide anyway.

          Deliberately above Changes and below Awaiting review: it is a standing
          fact about the deck, not an event, so it should not push the things
          you have to DECIDE down the panel. */}
      {(flagged.length > 0 || checking > 0) && (
        <div className="activity-panel__group">
          <div className="activity-panel__group-head">
            Layout issues
            {checking > 0 && (
              <span className="activity-panel__meta muted small" style={{ marginLeft: 'auto', fontWeight: 400 }}>
                {/* Never imply a clean bill of health while slides are still
                    unmeasured — an empty list would otherwise read as "no
                    problems" when it means "not looked yet". */}
                checking {checking} more…
              </span>
            )}
          </div>
          <ul className="activity-panel__list">
            {flagged.map(({ slide, findings }) => (
              <li
                key={slide.id}
                className="activity-panel__row activity-panel__row--clickable"
                onClick={() => onJumpToTurn?.({ kind: 'slide', slideId: slide.id })}
                title="Jump to this slide"
              >
                <span className="activity-panel__dot activity-panel__dot--issue" aria-hidden="true" />
                <div className="activity-panel__main">
                  <span className="activity-panel__label">
                    {String((slide.idx ?? 0) + 1).padStart(2, '0')} {slide.title || slide.id}
                  </span>
                  <span className="activity-panel__meta muted small">
                    {sentenceCase(findings.map(findingSummary).join(' · '))}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="activity-panel__group">
        <div className="activity-panel__group-head">
          Changes
          {settled.length > 0 && (
            <button
              type="button"
              className="link activity-panel__clear"
              onClick={() =>
                api
                  .post(`/api/chat/${slug}/history/clear`, {})
                  .then(() => notifyActivityChanged())
                  .catch(() => {})
              }
              title="Hide these finished changes from the list. Anything awaiting review stays."
            >
              clear
            </button>
          )}
        </div>
        {settled.length === 0 && awaiting.length === 0 && running.length === 0 && (
          <p className="muted small activity-panel__empty">
            No AI changes yet. Proposals and applied edits from chat land here, with Apply and Revert in one place.
          </p>
        )}
        {settled.length > 0 && (
          <ul className="activity-panel__list">
            {groupSettled(settled).map((item) =>
              item.kind === 'stack' ? (
                <SlideStack
                  key={item.key}
                  slug={slug}
                  stack={item}
                  title={slideTitle(item.slideId)}
                  undoCounts={undoCounts}
                  currentId={currentIds.get(item.slideId)}
                  onJump={() => {
                    // A stack is only built from rows, so it always has one.
                    const first = item.rows[0];
                    if (first) jump(first);
                  }}
                  onChanged={onChanged}
                />
              ) : (
                <ActivityRow
                  key={item.key}
                  slug={slug}
                  row={item.row}
                  title={slideTitle(item.row.slideId)}
                  undoCount={undoCounts.get(rowId(item.row)) || 1}
                  onJump={() => jump(item.row)}
                  onChanged={onChanged}
                />
              ),
            )}
          </ul>
        )}
      </div>
    </div>
  );
}

/**
 * One slide's changes as a version ladder: newest at the top, older ones folded
 * away until asked for. The spine and the leading timestamps carry the order —
 * that is the whole point of the group, so nothing else needs to say it.
 */
function SlideStack({
  slug,
  stack,
  title,
  undoCounts,
  currentId,
  onJump,
  onChanged,
}: {
  slug: string;
  stack: Stack;
  title: string;
  undoCounts: Map<string, number>;
  /** Which row the slide is actually showing — absent while it is under
   *  review, since the row under review has left the ladder. */
  currentId?: string;
  onJump: () => void;
  onChanged?: (notice?: ActionNotice) => void;
}) {
  const [open, setOpen] = useState(false);
  const { rows, redoId, changeCount, minShown } = stack;
  const shown = open ? rows : rows.slice(0, minShown || 1);
  const hidden = rows.length - shown.length;
  const idOf = rowId;

  return (
    <li className="activity-stack">
      <div
        className="activity-stack__head"
        onClick={onJump}
        title="Jump to this slide"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onJump();
          }
        }}
      >
        <span className="activity-panel__dot activity-panel__dot--applied" aria-hidden="true" />
        <span className="activity-stack__title">{title}</span>
        <span className="activity-stack__count">{changeCount === 1 ? '1 change' : `${changeCount} changes`}</span>
      </div>
      <ul className="activity-stack__ladder">
        {shown.map((row) => (
          <ActivityRow
            key={idOf(row)}
            slug={slug}
            row={row}
            title={title}
            variant="step"
            undoCount={undoCounts.get(idOf(row)) || 1}
            isCurrent={idOf(row) === currentId}
            canRedo={idOf(row) === redoId}
            onJump={onJump}
            onChanged={onChanged}
          />
        ))}
      </ul>
      {hidden > 0 && (
        <button type="button" className="activity-stack__more" onClick={() => setOpen(true)}>
          {hidden === 1 ? 'Show 1 earlier change' : `Show ${hidden} earlier changes`}
        </button>
      )}
      {open && rows.length > 1 && (
        <button type="button" className="activity-stack__more" onClick={() => setOpen(false)}>
          Show only the latest
        </button>
      )}
    </li>
  );
}

function ActivityRow({
  slug,
  row,
  title,
  onJump,
  onChanged,
  variant = 'row',
  undoCount = 1,
  isCurrent = false,
  canRedo = true,
}: {
  slug: string;
  row: Row;
  title: string;
  onJump: () => void;
  onChanged?: (notice?: ActionNotice) => void;
  /** 'step' renders inside a stack's ladder: no dot, no slide title, and the
   *  rationale promoted to the headline. */
  variant?: 'row' | 'step';
  undoCount?: number;
  isCurrent?: boolean;
  canRedo?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Which side is on the slide during a live review — SHARED with the on-slide
  // banner and the chat card (lib/reviewFlip.js), seeded from the server. It
  // was private state that only ever moved when YOU flipped it here, so the
  // banner could say "Now" while this row said "Before" about the same slide.
  const [showing] = useFlipShowing(slug, row.slideId, { seed: !row.pending });
  // 'stale' 409 → the slide changed since; offer a forced retry.
  const [confirmForce, setConfirmForce] = useState(false);
  // Undoing a CREATED slide removes it, and there is no "before" to review
  // first — so it asks before it acts.
  const [confirmRemove, setConfirmRemove] = useState(false);
  const step = variant === 'step';

  // What kind of live review (if any) this row is in:
  //  - pending                   → a NEW slide sitting in the deck for review
  //  - ready + previewing        → reviewing the PROPOSAL
  //  - applied + previewing      → reviewing what a REVERT would restore
  //  - reverted + previewing     → reviewing what a RE-APPLY would restore
  const review = row.pending
    ? 'create'
    : row.previewing
      ? row.status === 'ready'
        ? 'proposal'
        : row.status === 'applied'
          ? 'revert'
          : row.status === 'reverted'
            ? 'reapply'
            : null
      : null;

  // `notice` describes what just happened, for the surface that owns the toast
  // (Canvas). Reverting used to succeed in silence while the row dropped from
  // "Awaiting review" into "Changes" — correct, but it reads as the row
  // vanishing (Jake, 2026-07-30).
  async function run(fn: () => Promise<unknown>, notice?: ActionNotice) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged?.(notice);
    } catch (err) {
      if (errorCode(err) === 'stale') setConfirmForce(true);
      else setError(errorMessage(err) || 'Action failed');
    } finally {
      setBusy(false);
    }
  }

  // Every caller sits behind a `review` state that only a slide row can reach,
  // so the guard is belt-and-braces rather than a live case.
  const asSlideRow = (): SlideRow | null => (row.slideId ? ({ ...row, slideId: row.slideId } as SlideRow) : null);
  const flip = () => {
    const r = asSlideRow();
    if (r) run(() => toggleRow(slug, r));
  };

  const startRevertReview = (side: 'before' | 'after') =>
    run(async () => {
      await revertPreviewRow(slug, row, side); // sets the shared flip state
      onJump(); // show the slide being reviewed
    });

  const act = (fn: () => void) => (e: MouseEvent) => {
    e.stopPropagation();
    fn();
  };

  const time = row.ts ? new Date(row.ts).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '';
  // Inside a stack the slide title is the group head, so the rationale — the
  // one thing that differs between six edits to the same slide — is promoted
  // to the row's own headline.
  const headline = step ? row.label || labelFor(row, review) : title;
  // Inside a stack, "· applied" under every step is a line of noise: the ladder
  // says it. A step keeps a second line only when it carries something the
  // ladder doesn't — a verb other than Edit, or a state that isn't applied.
  const stepSub = () => {
    if (!row.label) return ''; // the headline already IS the label
    if (row.leftover || row.status !== 'applied') return labelFor(row, review);
    const verb = verbFor(row);
    return verb === 'Edit' ? '' : verb;
  };
  const sub = step ? stepSub() : `${labelFor(row, review)}${time ? ` · ${time}` : ''}`;
  // Every undo is named for what it does to the deck, not for which record it
  // acts on: undoing the third change down takes three changes off the slide.
  const undoLabel = undoCount > 1 ? `Undo last ${undoCount} changes` : 'Undo this change';
  const undoHint =
    undoCount > 1
      ? `Puts the slide back to how it was before this change — the ${undoCount - 1} later ${
          undoCount === 2 ? 'change goes' : 'changes go'
        } too. You review it first.`
      : 'Show the slide as it was before this change, then confirm';

  return (
    <li
      className={
        (step ? 'activity-step' : 'activity-panel__row activity-panel__row--clickable') +
        ' activity-panel__row--' +
        row.status
      }
      onClick={step ? undefined : onJump}
      title={step ? undefined : 'Jump to this slide'}
    >
      {step ? (
        // The rail is the ladder's spine made legible: when each change landed,
        // and which one the slide is actually showing.
        <span className="activity-step__rail">
          <span className="activity-step__time">{time}</span>
          {isCurrent && (
            <span className="activity-step__now" title="This is what the slide shows now">
              now
            </span>
          )}
        </span>
      ) : (
        <span
          className={'activity-panel__dot activity-panel__dot--' + (review ? 'previewing' : row.status)}
          aria-hidden="true"
        />
      )}
      <div className="activity-panel__main">
        <span className={step ? 'activity-step__headline' : 'activity-panel__label'}>{headline}</span>
        {sub && <span className="activity-panel__meta muted small">{sub}</span>}
        {!step && row.label && <span className="activity-panel__rationale muted small">{row.label}</span>}
        {error && <span className="error small">{error}</span>}

        {confirmForce && (
          <span className="activity-panel__confirm small" onClick={(e) => e.stopPropagation()}>
            This slide has been edited outside the AI history since. Going back will drop that edit too.
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={act(() =>
                run(
                  () =>
                    row.status === 'reverted'
                      ? confirmRevertRow(slug, row, { force: true, reapply: true })
                      : row.status === 'applied' || review === 'revert'
                        ? confirmRevertRow(slug, row, { force: true })
                        : revertRow(slug, row, { force: true }),
                  { kind: row.status === 'reverted' ? 'reapply' : 'revert', title },
                ).then(() => setConfirmForce(false)),
              )}
              disabled={busy}
            >
              Go ahead anyway
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={act(() => setConfirmForce(false))}>
              Keep it
            </button>
          </span>
        )}

        <div className="activity-panel__actions" onClick={(e) => e.stopPropagation()}>
          {/* A proposed new slide, already in the deck for review: no flip —
              the alternative to keeping it is that it isn't there. */}
          {review === 'create' && (
            <>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={act(() => run(() => rejectRow(slug, row)))}
                disabled={busy}
                title="Take this slide back out of the deck"
              >
                Discard slide
              </button>
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={act(() => run(() => applyRow(slug, row)))}
                disabled={busy}
              >
                Keep slide
              </button>
            </>
          )}

          {/* Live proposal review: flip + keep/discard */}
          {review === 'proposal' && (
            <>
              {/* Pills view, buttons decide — see ProposalBanner for the why. */}
              <FlipPills
                showing={showing}
                currentLabel="Before"
                candidateLabel="After"
                onToggle={act(flip)}
                disabled={busy}
              />
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={act(() => run(() => rejectRow(slug, row)))}
                disabled={busy}
              >
                Discard change
              </button>
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={act(() => run(() => applyRow(slug, row)))}
                disabled={busy}
              >
                Keep change
              </button>
            </>
          )}

          {/* Revert / re-apply review: flip + confirm/keep */}
          {(review === 'revert' || review === 'reapply') && (
            <>
              <FlipPills
                showing={showing}
                currentLabel="Now"
                candidateLabel={review === 'revert' ? 'Before' : 'Restored'}
                onToggle={act(flip)}
                disabled={busy}
              />
              {review === 'revert' && undoCount > 1 && (
                <span className="activity-panel__note muted small">
                  Restoring this also undoes the {undoCount - 1} later {undoCount === 2 ? 'change' : 'changes'} on this
                  slide.
                </span>
              )}
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={act(() => run(() => cancelRowPreview(slug, row)))}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={act(() =>
                  run(() => confirmRevertRow(slug, row, { reapply: review === 'reapply' }), {
                    kind: review === 'reapply' ? 'reapply' : 'revert',
                    title,
                  }),
                )}
                disabled={busy}
              >
                {review === 'revert' ? 'Restore old version' : 'Restore this version'}
              </button>
            </>
          )}

          {/* Ready but not previewing yet (auto-preview races, non-previewable tools) */}
          {!review && row.status === 'ready' && row.input && (
            <>
              {canPreviewRow(row) && (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={act(() =>
                    run(async () => {
                      // canPreviewRow already required a slide id; this states it.
                      const r = asSlideRow();
                      if (!r) return;
                      await previewRow(slug, r); // sets the shared flip state
                      onJump();
                    }),
                  )}
                  disabled={busy}
                >
                  Review on slide
                </button>
              )}
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={act(() => run(() => rejectRow(slug, row)))}
                disabled={busy}
              >
                Discard change
              </button>
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={act(() => run(() => applyRow(slug, row)))}
                disabled={busy}
              >
                Keep change
              </button>
            </>
          )}

          {/* Applied → start a revert review (see what you'd go back to).
              A CREATED slide has no earlier version to show — undoing it takes
              the slide out — so it asks straight out instead of trying to
              preview a "before" that never existed (which 409'd). */}
          {!review && row.status === 'applied' && row.revertible && !confirmForce && row.tool !== 'create_slide' && (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={act(() => startRevertReview('before'))}
              disabled={busy}
              title={undoHint}
            >
              {undoLabel}
            </button>
          )}

          {!review && row.status === 'applied' && row.revertible && !confirmForce && row.tool === 'create_slide' && (
            <>
              {!confirmRemove && (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={act(() => setConfirmRemove(true))}
                  disabled={busy}
                  title="Take this slide back out of the deck"
                >
                  Remove this slide
                </button>
              )}
              {confirmRemove && (
                <span className="activity-panel__confirm small">
                  {undoCount > 1
                    ? `Remove the slide, and the ${undoCount - 1} later ${
                        undoCount === 2 ? 'change' : 'changes'
                      } to it?`
                    : 'Remove this slide from the deck?'}
                  <button
                    type="button"
                    className="btn btn--primary btn--sm"
                    onClick={act(() =>
                      run(() => revertRow(slug, row), { kind: 'revert', title }).then(() => setConfirmRemove(false)),
                    )}
                    disabled={busy}
                  >
                    Remove it
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={act(() => setConfirmRemove(false))}
                    disabled={busy}
                  >
                    Keep it
                  </button>
                </span>
              )}
            </>
          )}

          {/* Reverted → start a re-apply review. In a stack only the oldest
              undone change offers this: the slide holds ITS pre-change source,
              so it is the only redo that lands where it was recorded. */}
          {!review && row.status === 'reverted' && row.reapplyable && canRedo && !confirmForce && (
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={act(() => startRevertReview('after'))}
              disabled={busy}
              title="Show the slide with this change put back, then confirm"
            >
              Redo this change
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

const verbFor = (row: Row): string =>
  ({
    edit_slide: 'Edit',
    replace_slide_content: 'Edit',
    swap_primitive: 'Edit',
    create_slide: 'New slide',
    delete_slide: 'Delete',
    reorder_slides: 'Reorder',
    reorder_sections: 'Reorder sections',
    create_section: 'New section',
    delete_section: 'Delete section',
    turn_error: 'AI run',
    turn_stopped: 'AI run',
  })[row.tool] || sentenceCase(row.tool);

type Review = 'create' | 'proposal' | 'revert' | 'reapply' | null;

function labelFor(row: Row, review: Review): string {
  const verb = verbFor(row);
  // A change nobody chose: an interrupted run left it on the slide and
  // containment couldn't safely undo it. Say that, rather than "applied",
  // which would imply the user approved it. It outranks the review states
  // below because a leftover is never a decision anyone is being asked to make.
  const status = row.leftover
    ? row.status === 'reverted'
      ? 'Put back'
      : 'Left changed · revert to undo'
    : review === 'create'
      ? 'In the deck for review'
      : review === 'proposal'
        ? 'Reviewing on slide'
        : review === 'revert'
          ? 'Reviewing revert'
          : review === 'reapply'
            ? 'Reviewing re-apply'
            : row.tool === 'turn_stopped'
              ? 'Stopped by you'
              : { ready: 'Proposed', applied: 'Applied', rejected: 'Rejected', reverted: 'Undone', error: 'Failed' }[
                  row.status
                ] || sentenceCase(row.status);
  return `${verb} · ${status}`;
}
